import { useCallback, useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { useLocation, useParams, useSearchParams } from "react-router-dom";
import { getCustomFormData, getGroupFormByGroup, getSurveyFormDataByCode, saveSurveyData, submitCustomForm, submitCustomFormNew } from "../../api/customForm";
import { Form, message } from "antd";
import { CloseCircleOutlined } from '@ant-design/icons';
import { checkWarrantyPeriod, sendSystemMessage, verifyOtp } from "../../api/warranty";
import { getMerchantCode } from "../../components/customFormComponents/utils/commonFun"
import { useFormRecaptcha } from "../../hooks/useFormRecaptcha";

export const useCustomForm = props =>{

    const { fCode, mCode, fType, embedLanguageSync, embedPageLanguage, onEmbedFormChange } = props;

    const [messageApi, contextHolder] = message.useMessage();
    const { formatMessage } = useIntl();
    const {pathname} = useLocation();
    const {form_type, merchant_code} = useParams();
    const [searchParams] = useSearchParams();
    const code = searchParams.get('code');
    const languageId = searchParams.get('language');

    const [customFormCode, setCustomFormCode] = useState(null);
    const [currentMerchantCode, setCurrentMerchantCode] = useState(null);
    const [customFormType, setCustomFormType] = useState(null);

    const [custom_form] = Form.useForm();
    const [loading, setLoading] = useState(true);
    const [formData, setFormData] = useState({});
    const [fieldItems, setFieldItems] = useState([]);
    const [showSubmitSuccess, setShowSubmitSuccess] = useState(false);
    
    const [canSendOtp, setCanSendOtp] = useState([]);
    const [waitOtpSeconds, setWaitOtpSeconds] = useState([]);
    const [otpLoading, setOtpLoading] = useState([]);
    const [validateOtpLoading, setValidateOtpLoading] = useState([]);
    const [validateOtpResult, setValidateOtpResult] = useState([]);
    const [validateOtpValue, setValidateOtpValue] = useState([]);

    const [allFieldValues, setAllFieldValues] = useState({});
    const [formClientWidth, setFormClientWidth] = useState(1024);
    
    const [errorMessage, setErrorMessage] = useState(false);
    const [successMessage, setSuccessMessage] = useState(false);

    const [htmlData, setHtmlData] = useState({
        title: null,
        favicon: null
    })

    const [isEmbed, setIsEmbed] = useState(false);
    const [languageOptions, setLanguageOptions] = useState([]);
    const [selectedLanguageCode, setSelectedLanguageCode] = useState(null);
    const [showLanguageSelector, setShowLanguageSelector] = useState(false);
    const [recaptchaConfig, setRecaptchaConfig] = useState(null);
    const isEmbedMode = !!(fCode && mCode && fType);
    // [EMBED_LANG_SYNC] When enabled, language is controlled by host page and selector should be hidden.
    const enableEmbedLanguageSync = !!(isEmbedMode && embedLanguageSync);

    // [reCAPTCHA] reCAPTCHA config is read from the public form schema.
    const recaptcha = useFormRecaptcha(recaptchaConfig);

    // [EMBED_LANG_SYNC] Redirect to target form route by code/type.
    const handleRedirectToForm = useCallback((targetCode, targetType, targetLanguageCode) => {
        const merchantCodeValue = merchant_code || mCode;
        const currentCode = code || fCode;
        const currentType = form_type || fType;
        if(!targetCode || !targetType || !merchantCodeValue) {
            return;
        }
        if(currentCode === targetCode && currentType === targetType) {
            return;
        }
        // [EMBED_LANG_SYNC] In embed mode, switch fCode/fType via props updater instead of page redirect.
        if(isEmbedMode && typeof onEmbedFormChange === "function") {
            onEmbedFormChange({
                fCode: targetCode,
                fType: targetType
            });
            return;
        }
        const nextSearchParams = new URLSearchParams(searchParams.toString());
        nextSearchParams.set("code", targetCode);
        // if(targetLanguageCode) {
        //     nextSearchParams.set("language", targetLanguageCode);
        // }
        const nextUrl = `/custom-form/${targetType}/${merchantCodeValue}/view?${nextSearchParams.toString()}`;
        window.location.href = nextUrl;
    }, [merchant_code, mCode, code, fCode, form_type, fType, searchParams, isEmbedMode, onEmbedFormChange]);

    const handleGetLanguageOptions = useCallback(async (group, languageIso, m_code) => {
        if(!group) {
            setLanguageOptions([]);
            setSelectedLanguageCode(null);
            setShowLanguageSelector(false);
            return Promise.resolve([]);
        }
        const merchantCode = getMerchantCode(m_code, 'form');
        try {
            const res = await getGroupFormByGroup(group, merchantCode || (m_code === 'uat_test' ? '' : m_code), merchantCode ? null : 'form' );
            const options = res?.results?.map(item => ({
                value: item?.languageIso,
                label: item?.languageLabel,
                lan_code: item?.languageCode,
                code: item?.code,
                type: item?.type
            })).filter(item_1 => item_1.value && item_1.label) || [];
            setLanguageOptions(options);
            if (options?.length) {
                const selectedOption = options.find(item_2 => item_2.value === languageIso) || 
                    options.find(item_2 => item_2.lan_code === 'default') || options[0];
                setSelectedLanguageCode(selectedOption?.value || null);
            }
            else {
                setSelectedLanguageCode(null);
            }
            return options;
        } catch {
            setLanguageOptions([]);
            setSelectedLanguageCode(null);
            setShowLanguageSelector(false);
            return [];
        }
    }, []);

    //获取表单数据
    const handleGetFormData = useCallback((f_code, m_code, f_type) => {
        setLoading(true);
        if(f_type === 'survey') {
            setLanguageOptions([]);
            setSelectedLanguageCode(null);
            setShowLanguageSelector(false);
            const merchantCode = m_code === 'uat_test' ? '' : `api/merchant/${m_code}/survey/`;
            getSurveyFormDataByCode({
                code: f_code
            }, merchantCode).then(res => {
                if(res.data) {
                    setFormData(res.data?.content);
                    let field_items = [];
                    let can_send_otp = [];
                    let wait_otp_seconds = [];
                    let otp_loading = [];
                    let validate_otp_loading = [];
                    let validate_otp_result = [];
                    let validate_otp_value = [];
                    let all_field_values = {};
                    for(let key in res.data?.content) {
                        if(res.data?.content[key]?.isFormItem && res.data?.content[key]?.data?.props?.name) {
                            field_items.push({
                                field: res.data?.content[key]?.data?.props?.name,
                                type: res.data?.content[key]?.type,
                                pickerType: res.data?.content[key]?.data?.props?.type,
                                pickerFormat: res.data?.content[key]?.data?.props?.format,
                                isChecked: res.data?.content[key]?.data?.props?.checked,
                                isGroup: res.data?.content[key]?.data?.props?.isGroup,
                                isMultiple: res.data?.content[key]?.data?.props?.multiple,
                                checkedOptions: res.data?.content[key]?.data?.props?.checkedOptions || [],
                                inputFormat: res.data?.content[key]?.data?.props?.format || [],
                                otherCustom: res.data?.content[key]?.otherCustom || null
                            });
                            all_field_values[res.data?.content[key]?.data?.props?.name] = {
                                value: res.data?.content[key]?.data?.props?.defaultValue,
                                type: res.data?.content[key]?.type,
                                isRequired: res.data?.content[key]?.data?.props?.required
                            };
                            if(res.data?.content[key]?.data?.prefixProps?.showPreFix) {
                                field_items.push({
                                    field: res.data?.content[key]?.data?.prefixProps?.name,
                                    type: res.data?.content[key]?.data?.prefixProps?.type,
                                    checkedOptions: [],
                                    inputFormat: [],
                                });
                                all_field_values[res.data?.content[key]?.data?.prefixProps?.name] = {
                                    value: undefined,
                                    type: res.data?.content[key]?.data?.prefixProps?.type,
                                    isRequired: res.data?.content[key]?.data?.prefixProps?.required
                                };
                            }
                        }
                        if(res.data?.content[key]?.type === 'OTP' && res.data?.content[key]?.data?.props?.name) {
                            can_send_otp.push({
                                field: res.data?.content[key]?.data?.props?.name,
                                inputField: res.data?.content[key]?.data?.relatedProps?.field,
                                inputValue: undefined,
                                canSend: false
                            });
                            wait_otp_seconds.push({
                                field: res.data?.content[key]?.data?.props?.name,
                                countdown: 0
                            })
                            otp_loading.push({
                                field: res.data?.content[key]?.data?.props?.name,
                                loading: false
                            })
                            validate_otp_loading.push({
                                field: res.data?.content[key]?.data?.props?.name,
                                relatedField: res.data?.content[key]?.data?.relatedProps?.field,
                                loading: false
                            })
                            validate_otp_result.push({
                                field: res.data?.content[key]?.data?.props?.name,
                                res: null
                            })
                            validate_otp_value.push({
                                field: res.data?.content[key]?.data?.props?.name,
                                invalidMessage: res.data?.content[key]?.data?.messageProps?.invalidOtpLabel,
                                value: null
                            })
                        }
                    }
                    const can_send_otp1 = can_send_otp.map(item=>{
                        const i_format = field_items.find(f_item=>f_item.field === item.inputField)?.inputFormat;
                        return {
                            ...item,
                            inputFormat: i_format
                        }
                    })
                    setFieldItems(field_items);
                    setCanSendOtp(can_send_otp1);
                    setOtpLoading(otp_loading);
                    setValidateOtpLoading(validate_otp_loading);
                    setWaitOtpSeconds(wait_otp_seconds);
                    setValidateOtpResult(validate_otp_result);
                    setValidateOtpValue(validate_otp_value);
                    setAllFieldValues(all_field_values);
                    if(custom_form) {
                        Object.keys(all_field_values).forEach(key => {
                            custom_form.setFieldValue(key, all_field_values[key].value);
                        });
                    }
                }
                else {
                    messageApi.open({
                        type: 'error',
                        content: res.message,
                    });
                }
                setLoading(false);
            }).catch(error => {
                setLoading(false);
                messageApi.open({
                    type: 'error',
                    content: error.response?.data?.message || error.message,
                });
            })
        }
        else {
            const merchantCode = getMerchantCode(m_code, 'form');
            getCustomFormData({
                formCode: f_code,
                locale: 'global'
            }, merchantCode ? merchantCode : m_code === 'uat_test' ? '' : m_code, merchantCode ? null : 'form').then(res => {
                if(res?.status === '200') {
                    const group = res?.data?.group;
                    const languageIso = res?.data?.language;
                    if(group && languageIso) {
                        setSelectedLanguageCode(languageIso);
                        handleGetLanguageOptions(group, languageIso, m_code).then(options => {
                            const shouldShowSelector = !enableEmbedLanguageSync && options?.length;
                            setShowLanguageSelector(!!shouldShowSelector);
                            if(enableEmbedLanguageSync && embedPageLanguage && options?.length > 0) {
                                const targetLanguage = options.find(item => item.value === embedPageLanguage) || 
                                    options.find(item => item.lan_code === 'default') || options[0];
                                if(targetLanguage?.code && targetLanguage?.type) {
                                    handleRedirectToForm(targetLanguage.code, targetLanguage.type, embedPageLanguage);
                                }
                            }
                        });
                    }
                    else {
                        setLanguageOptions([]);
                        setSelectedLanguageCode(null);
                        setShowLanguageSelector(false);
                    }
                    setHtmlData({
                        title: res.data?.title || null,
                        favicon: res.data?.favicon || null
                    })
                    setFormData(res.data?.form);
                    // [reCAPTCHA] read reCAPTCHA runtime config from public schema.
                    setRecaptchaConfig(res.data?.recaptcha || null);
                    let field_items = [];
                    let can_send_otp = [];
                    let wait_otp_seconds = [];
                    let otp_loading = [];
                    let validate_otp_loading = [];
                    let validate_otp_result = [];
                    let validate_otp_value = [];
                    let all_field_values = {};
                    for(let key in res.data?.form) {
                        if(res.data?.form[key]?.isFormItem && res.data?.form[key]?.data?.props?.name) {
                            field_items.push({
                                field: res.data?.form[key]?.data?.props?.name,
                                type: res.data?.form[key]?.type,
                                pickerType: res.data?.form[key]?.data?.props?.type,
                                pickerFormat: res.data?.form[key]?.data?.props?.format,
                                isChecked: res.data?.form[key]?.data?.props?.checked,
                                isGroup: res.data?.form[key]?.data?.props?.isGroup,
                                isMultiple: res.data?.form[key]?.data?.props?.multiple,
                                checkedOptions: res.data?.form[key]?.data?.props?.checkedOptions || [],
                                inputFormat: res.data?.form[key]?.data?.props?.format || [],
                                otherCustom: res.data?.form[key]?.otherCustom || null
                            });
                            const url_default_val = searchParams.get(res.data?.form[key]?.data?.props?.name);
                            all_field_values[res.data?.form[key]?.data?.props?.name] = {
                                value: url_default_val || res.data?.form[key]?.data?.props?.defaultValue,
                                type: res.data?.form[key]?.type,
                                isRequired: res.data?.form[key]?.data?.props?.required
                            };
                            if(res.data?.form[key]?.data?.prefixProps?.showPreFix) {
                                field_items.push({
                                    field: res.data?.form[key]?.data?.prefixProps?.name,
                                    type: res.data?.form[key]?.data?.prefixProps?.type,
                                    checkedOptions: [],
                                    inputFormat: [],
                                });
                                all_field_values[res.data?.form[key]?.data?.prefixProps?.name] = {
                                    value: undefined,
                                    type: res.data?.form[key]?.data?.prefixProps?.type,
                                    isRequired: res.data?.form[key]?.data?.prefixProps?.required
                                };
                            }
                        }
                        if(res.data?.form[key]?.type === 'OTP' && res.data?.form[key]?.data?.props?.name) {
                            can_send_otp.push({
                                field: res.data?.form[key]?.data?.props?.name,
                                inputField: res.data?.form[key]?.data?.relatedProps?.field,
                                inputValue: undefined,
                                canSend: false
                            });
                            wait_otp_seconds.push({
                                field: res.data?.form[key]?.data?.props?.name,
                                countdown: 0
                            })
                            otp_loading.push({
                                field: res.data?.form[key]?.data?.props?.name,
                                loading: false
                            })
                            validate_otp_loading.push({
                                field: res.data?.form[key]?.data?.props?.name,
                                relatedField: res.data?.form[key]?.data?.relatedProps?.field,
                                loading: false
                            })
                            validate_otp_result.push({
                                field: res.data?.form[key]?.data?.props?.name,
                                res: null
                            })
                            validate_otp_value.push({
                                field: res.data?.form[key]?.data?.props?.name,
                                invalidMessage: res.data?.form[key]?.data?.messageProps?.invalidOtpLabel,
                                value: null
                            })
                        }
                    }
                    const can_send_otp1 = can_send_otp.map(item=>{
                        const i_format = field_items.find(f_item=>f_item.field === item.inputField)?.inputFormat;
                        return {
                            ...item,
                            inputFormat: i_format
                        }
                    })
                    setFieldItems(field_items);
                    setCanSendOtp(can_send_otp1);
                    setOtpLoading(otp_loading);
                    setValidateOtpLoading(validate_otp_loading);
                    setWaitOtpSeconds(wait_otp_seconds);
                    setValidateOtpResult(validate_otp_result);
                    setValidateOtpValue(validate_otp_value);
                    setAllFieldValues(all_field_values);
                    if(custom_form) {
                        Object.keys(all_field_values).forEach(key => {
                            if(all_field_values[key].type === "Phone_with_area") {
                                const phone_val = all_field_values[key].value ? all_field_values[key].value.split('-') : [];
                                if(phone_val?.length) {
                                    custom_form.setFieldValue(key+"_area", phone_val[0]);
                                    if(phone_val.length > 1) {
                                        custom_form.setFieldValue(key+"_phone", phone_val[1]);
                                    }
                                    else {
                                        custom_form.setFieldValue(key+"_phone", '');
                                    }
                                }
                                else {
                                    custom_form.setFieldValue(key+"_area", '');
                                    custom_form.setFieldValue(key+"_phone", '');
                                }
                            }
                            else {
                                custom_form.setFieldValue(key, all_field_values[key].value);
                            }
                        });
                    }
                }
                else {
                    messageApi.open({
                        type: 'error',
                        content: res.message,
                    });
                }
                setLoading(false);
            }).catch(error=>{
                setLoading(false);
                messageApi.open({
                    type: 'error',
                    content: error.response?.data?.message || error.message,
                });
            })
        }
    }, [custom_form, messageApi, searchParams, handleGetLanguageOptions, enableEmbedLanguageSync, embedPageLanguage, handleRedirectToForm])

    const handleChangeLanguage = useCallback((languageIso) => {
        if(enableEmbedLanguageSync) {
            return;
        }
        setSelectedLanguageCode(languageIso);
        const targetLanguage = languageOptions.find(item => item.value === languageIso);
        if(targetLanguage?.code && targetLanguage?.type) {
            handleRedirectToForm(targetLanguage.code, targetLanguage.type, languageIso);
        }
    }, [enableEmbedLanguageSync, languageOptions, handleRedirectToForm]);

    //提交问卷
    const handleSubmitSurvey = useCallback((values)=>{
        const save_values = values ? JSON.parse(JSON.stringify(values)) : null;
        const properties = formData?.schema?.properties;
        let finalValues = {};
        Object.keys(properties).forEach(key=>{
            if(properties[key]?.name !== 'submit' && properties[key]?.name) {
                finalValues[key] = {
                    label: properties[key]?.title,
                    type: properties[key]?.['x-component'],
                    value: save_values[key] === null || save_values[key] === undefined ? undefined : save_values[key]
                }
            }
        })
        setLoading(true);
        const merchantCode = currentMerchantCode === 'uat_test' ? '' : `api/merchant/${currentMerchantCode}/survey/`;
        if(save_values) {
            saveSurveyData({
                data: {
                    surveyCode: customFormCode,
                    result: finalValues
                }
            }, merchantCode).then(res => {
                if(res?.status === '200') {
                    //跳转填写成功页面
                    setShowSubmitSuccess(true);
                }
                else {
                    messageApi.open({
                        type: 'error',
                        content: res.message,
                    });
                }
                setLoading(false);
            }).catch(error => {
                setLoading(false);
                messageApi.open({
                    type: 'error',
                    content: error.response?.data?.message || error.message,
                });
            })
        }
    }, [customFormCode, formData, currentMerchantCode, messageApi]);

    function parseValidator(str) {
        const match = str.match(/^\/(.+)\/([gimsuy]*)$/);
        return match ? new RegExp(match[1], match[2]) : new RegExp(str);
    }

    //input验证
    const handleValidateInput = useCallback((formats, val)=>{
        let res = true;
        formats?.forEach(item=>{
            const validate_str = parseValidator(item.value);
            if(!validate_str?.test(val)) {
                res = false;
            }
        })
        return res;
    }, [])

    //check_warranty_status 验证成功后发送短信
    const handleSendMessage = useCallback((values) => {
        const code = currentMerchantCode === 'uat_test' ? '' : `api/merchant/${currentMerchantCode}/warranty/`;
        sendSystemMessage({
            type: "query-form",
            id: values.email,
            content: values,
            merchant: currentMerchantCode === 'uat_test' ? 'demo' : currentMerchantCode,
            language: languageId
        }, code).then(res=>{
            setLoading(false);
            if(res?.status == '200') {
                const rootIds = Object.keys(formData)?.filter((key) => key.includes('root'));
                const message = rootIds?.length ? formData?.[rootIds[0]]?.data?.messageData?.successfully : null;
                setSuccessMessage(message || formatMessage({
                    id: "warrantyPeriod.sendSuccessfulLabel", 
                    defaultMessage: "An email has been successfully sent to the provided email address. Please check your inbox."
                }))
            }
            else {
                setErrorMessage(res.message);
            }
        }).catch(error=>{
            setErrorMessage(error.response?.data?.message || error.message);
            setSuccessMessage(null);
            setLoading(false);
        })
    }, [formatMessage, languageId, currentMerchantCode, formData])

    const handleResetFormData = useCallback((resetFields)=>{
        let all_field_values = {};
        for(let key in formData) {
            if(formData[key]?.isFormItem && formData[key]?.data?.props?.name) {
                const url_default_val = searchParams.get(formData[key]?.data?.props?.name);
                all_field_values[formData[key]?.data?.props?.name] = {
                    value: url_default_val || formData[key]?.data?.props?.defaultValue || undefined,
                    type: formData[key]?.type,
                    isRequired: formData[key]?.data?.props?.required
                };
                if(formData[key]?.data?.prefixProps?.showPreFix) {
                    all_field_values[formData[key]?.data?.prefixProps?.name] = {
                        value: undefined,
                        type: formData[key]?.data?.prefixProps?.type,
                        isRequired: formData[key]?.data?.prefixProps?.required
                    };
                }
            }
        }
        setAllFieldValues(all_field_values);
        if(custom_form) {
            Object.keys(all_field_values).forEach(key => {
                if(all_field_values[key].type === "Phone_with_area") {
                    const phone_val = all_field_values[key].value ? all_field_values[key].value.split('-') : [];
                    if(phone_val?.length) {
                        custom_form.setFieldValue(key+"_area", phone_val[0]);
                        if(phone_val.length > 1) {
                            custom_form.setFieldValue(key+"_phone", phone_val[1]);
                        }
                        else {
                            custom_form.setFieldValue(key+"_phone", '');
                        }
                    }
                    else {
                        custom_form.setFieldValue(key+"_area", '');
                        custom_form.setFieldValue(key+"_phone", '');
                    }
                }
                else {
                    if(!all_field_values[key].value) {
                        custom_form.resetFields([key]);
                    }
                    else {
                        custom_form.setFieldValue(key, all_field_values[key].value);
                    }
                }
            });
            resetFields?.forEach(r_item=>{
                custom_form.setFieldValue(r_item, undefined);
            })
        }
    }, [custom_form, formData, searchParams]);

    //显示display data
    const handleGetFieldFormat = useCallback((format_values)=>{
        const fieldProps = {};
        Object.values(formData || {}).forEach(item => {
            if(item?.isFormItem && item?.data?.props?.name) {
                fieldProps[item.data.props.name] = item.data.props;
                if(item.data.prefixProps?.showPreFix) {
                    fieldProps[item.data.prefixProps.name] = item.data.prefixProps;
                }
            }
        });

        const toDisplayValue = (value, options = []) => {
            if(value == null) return '';
            if(Array.isArray(value)) {
                return value.map(item => toDisplayValue(item, options)).join(', ');
            }
            const option = options.find(item => (item?.value ?? item) === value);
            if(option != null) return String(option.label ?? option.value ?? option);
            return typeof value === 'object' ? JSON.stringify(value) : String(value);
        };

        return {
            fields: fieldItems.flatMap(item => {
                const key = item.field;
                const match = key.match(/^([^\[\]]+)\[([^\[\]]+)\]$/);
                const values = match ? format_values[match[1]] : format_values;
                const valueKey = match ? match[2] : key;
                if(!values || !Object.prototype.hasOwnProperty.call(values, valueKey)) return [];
                const props = fieldProps[key] || {};
                return [{
                    key,
                    label: props.label || key,
                    displayValue: toDisplayValue(values[valueKey], props.options || []),
                }];
            }),
        };
    }, [fieldItems, formData]);

    //提交自定义表单
    const handleSubmitCustomForm = useCallback(async (values)=>{
        if(validateOtpValue?.length && validateOtpValue?.find(item=>!item.value)) {
            const invalid_otp = validateOtpValue?.filter(item=>!item.value);
            invalid_otp?.forEach((item, index)=>{
                const message_key = `custom_message_${Date.now()}_${index}`;
                messageApi.open({
                    key: message_key,
                    type: 'error',
                    content: <div style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr auto',
                        gap: '8px',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                        {item.invalidMessage}
                        <div style={{
                            cursor: 'pointer'
                        }} onClick={()=>{
                            messageApi.destroy(message_key)
                        }}>
                            <CloseCircleOutlined style={{color: "#D54941"}} />
                        </div>
                    </div>,
                    duration: 0, // 设置为0表示不自动关闭
                });
            })
            return;
        }
        let new_values = {};
        let dynamic_values = {};
        let resetFields = [];
        for(let key in allFieldValues) {
            const regex = /^([a-zA-Z0-9_-]+)_dynamic_([a-zA-Z0-9_-]+)$/;
            if(regex.test(key)) {
                //是dynamic的字段，特殊传值
                const dynamic_fields = key.split('_dynamic_');
                const before_field = dynamic_fields[0]; // 前面的字符
                const after_field = dynamic_fields[1];  // 后面的字符
                dynamic_values[before_field] = {
                    ...dynamic_values[before_field] || {},
                    [after_field]: custom_form.getFieldValue(key)
                }
                resetFields.push(key);
            }
            else {
                new_values[key] = custom_form.getFieldValue(key);
            }
        }
        new_values = {
            ...new_values,
            ...dynamic_values
        }
        let save_values = {};
        for(let key in new_values) {
            const field_item = fieldItems.find(item=>item.field === key);
            if(field_item?.type === 'Picker') {
                if(field_item?.pickerType === 'Range') {
                    if(new_values[key]?.length === 2) {
                        save_values[key] = [
                            new_values[key][0] ? new_values[key][0].format(field_item?.pickerFormat || 'YYYY-MM-DD') : '',
                            new_values[key][1] ? new_values[key][1].format(field_item?.pickerFormat || 'YYYY-MM-DD') : ''
                        ]
                    }
                    else if(new_values[key]?.length === 1) {
                        save_values[key] = [
                            new_values[key][0] ? new_values[key][0].format(field_item?.pickerFormat || 'YYYY-MM-DD') : '',
                            ''
                        ]
                    }
                    else {
                        save_values[key] = [
                            '',
                            ''
                        ]
                    }
                }
                else {
                    save_values[key] = new_values[key] ? new_values[key].format(field_item?.pickerFormat || 'YYYY-MM-DD') : '';
                }
            }
            else if(field_item?.type === 'TimePicker') {
                if(field_item?.pickerType === 'Range') {
                    if(new_values[key]?.length === 2) {
                        save_values[key] = [
                            new_values[key][0] ? new_values[key][0].format(field_item?.pickerFormat || 'HH:mm:ss') : '',
                            new_values[key][1] ? new_values[key][1].format(field_item?.pickerFormat || 'HH:mm:ss') : ''
                        ]
                    }
                    else if(new_values[key]?.length === 1) {
                        save_values[key] = [
                            new_values[key][0] ? new_values[key][0].format(field_item?.pickerFormat || 'HH:mm:ss') : '',
                            ''
                        ]
                    }
                    else {
                        save_values[key] = [
                            '',
                            ''
                        ]
                    }
                }
                else {
                    save_values[key] = new_values[key] ? new_values[key].format(field_item?.pickerFormat || 'HH:mm:ss') : '';
                }
            }
            else if(field_item?.type === 'Checkbox' && field_item.isChecked) {
                if(field_item.isGroup === 'No') {
                    save_values[key] = new_values[key] ? field_item.checkedOptions : [];
                }
                else {
                    save_values[key] = new_values[key];
                }
            }
            else if(field_item?.type === 'OTP') {
                save_values[key] = validateOtpValue?.find(item=>item.field === field_item.field)?.value;
            }
            else if(field_item?.type === 'Select' || field_item?.type === 'Checkbox' || field_item?.type === 'Slider') {
                //判断是否是数组格式的数据
                if(Array.isArray(new_values[key])) {
                    save_values[key] = customFormType === 'survey' ? (new_values[key]?.length ? new_values[key].join(',') : '') : new_values[key];
                }
                else {
                    save_values[key] = new_values[key];
                }
            }
            else if(field_item?.type === 'Cascader') {
                if(field_item?.isMultiple) {
                    save_values[key] = new_values[key]?.map(inner => inner.join("|")).join(",");
                }
                else if(Array.isArray(new_values[key])){
                    save_values[key] = new_values[key]?.length ? new_values[key].join('|') : '';
                }
                else {
                    save_values[key] = new_values[key];
                }
            }
            else if(field_item?.type === 'Phone_with_area') {
                const area_val = custom_form.getFieldValue(key+"_area");
                const phone_val = custom_form.getFieldValue(key+"_phone");
                save_values[key] = `${area_val}-${phone_val}`;
            }
            else if(!field_item?.otherCustom?.displayOnly){
                save_values[key] = new_values[key];
            }
        }
        let format_values = {};
        for(let key in save_values) {
            const match = key.match(/^([^\[\]]+)\[([^\[\]]+)\]$/);
            if(match) {
                const [, x, y] = match;
                if (!format_values[x]) {
                    format_values[x] = {};
                }

                format_values[x][y] = save_values[key];
            }
            else {
                format_values[key] = save_values[key];
            }
        }
        const displayData = handleGetFieldFormat(format_values);
        setLoading(true);
        // [reCAPTCHA] obtain proof (v2/v3) before sending the submission.
        let recaptchaProof = null;
        try {
            recaptchaProof = await recaptcha.getSubmitProof();
        } catch (e) {
            setLoading(false);
            messageApi.open({
                type: 'error',
                content: e?.message || 'reCAPTCHA verification failed',
            });
            return;
        }
        if(customFormType === 'survey') {
            let finalValues = {};
            Object.keys(formData).forEach(key=>{
                if(formData[key]?.isFormItem && formData[key]?.data?.props?.name) {
                    finalValues[key] = {
                        label: formData[key]?.data?.props?.label,
                        type: formData[key]?.type,
                        value: save_values[formData[key]?.data?.props?.name]
                    }
                }
            })
            const merchantCode1 = currentMerchantCode === 'uat_test' ? '' : `api/merchant/${currentMerchantCode}/survey/`;
            saveSurveyData({
                data: {
                    surveyCode: customFormCode,
                    result: finalValues
                },
                ...(recaptchaProof ? { recaptcha: recaptchaProof } : {}),
            }, merchantCode1).then(res => {
                if(res?.status === '200') {
                    //跳转填写成功页面
                    setShowSubmitSuccess(true);
                }
                else {
                    messageApi.open({
                        type: 'error',
                        content: res.message,
                    });
                    setValidateOtpResult(prevData=>prevData.map(prev=>{
                        return {
                            ...prev,
                            res: null
                        }
                    }));
                    setValidateOtpValue(prevData=>prevData.map(prev=>{
                        return {
                            ...prev,
                            value: null
                        }
                    }));
                    setCanSendOtp(prevData=>prevData.map(prev=>{
                        custom_form.setFieldValue(prev.field, undefined);
                        const wait_otp_second = waitOtpSeconds.find(item=>item.field === prev.field);
                        if(!wait_otp_second.countdown && prev.inputValue && handleValidateInput(prev.inputFormat, prev.inputValue)) {
                            return {
                                ...prev,
                                canSend: true
                            }
                        }
                        else {
                            return {
                                ...prev,
                                canSend: false
                            }
                        }
                    }));
                }
                setLoading(false);
            }).catch(error => {
                setLoading(false);
                messageApi.open({
                    type: 'error',
                    content: error.response?.data?.message || error.message,
                });
                setValidateOtpResult(prevData=>prevData.map(prev=>{
                    return {
                        ...prev,
                        res: null
                    }
                }));
                setValidateOtpValue(prevData=>prevData.map(prev=>{
                    return {
                        ...prev,
                        value: null
                    }
                }));
                setCanSendOtp(prevData=>prevData.map(prev=>{
                    custom_form.setFieldValue(prev.field, undefined);
                    const wait_otp_second = waitOtpSeconds.find(item=>item.field === prev.field);
                    if(!wait_otp_second.countdown && prev.inputValue && handleValidateInput(prev.inputFormat, prev.inputValue)) {
                        return {
                            ...prev,
                            canSend: true
                        }
                    }
                    else {
                        return {
                            ...prev,
                            canSend: false
                        }
                    }
                }));
            })
        }
        else if(customFormType === 'check_warranty_status') {
            setErrorMessage(null);
            setSuccessMessage(null);
            const code = currentMerchantCode === 'uat_test' ? '' : `api/merchant/${currentMerchantCode}/warranty/`;
            const warrantyBody = {
                ...values,
                ...(recaptchaProof ? { recaptcha: recaptchaProof } : {}),
            };
            checkWarrantyPeriod(warrantyBody, code).then(res => {
                if(res && res.emailAddress) {
                    handleSendMessage(values);
                }
                else {
                    const rootIds = Object.keys(formData)?.filter((key) => key.includes('root'));
                    const message = rootIds?.length ? formData?.[rootIds[0]]?.data?.messageData?.noRecord : null;
                    setErrorMessage(message || formatMessage({
                        id: "warrantyPeriod.checkNoRecordLabel", 
                        defaultMessage: "Sorry, no record was found!"
                    }));
                    setLoading(false);
                }
            }).catch(error=>{
                messageApi.open({
                    type: 'error',
                    content: error.response?.data?.message || error.message
                });
                setErrorMessage(error.response?.data?.message || error.message);
                setLoading(false);
            })
        }
        // else if(customFormType === 'booking' || customFormType === 'general') {
        else {
            const merchantCode2 = getMerchantCode(currentMerchantCode, 'form');
            const mCodeParam = merchantCode2 ? merchantCode2 : currentMerchantCode;
            const sCodeParam = merchantCode2 ? null : 'form';

            // Helper to reset OTP-related state after a submit attempt.
            const resetOtpState = (clearOtpField) => {
                setValidateOtpResult(prevData=>prevData.map(prev=>({ ...prev, res: null })));
                setValidateOtpValue(prevData=>prevData.map(prev=>({ ...prev, value: null })));
                setCanSendOtp(prevData=>prevData.map(prev=>{
                    if(clearOtpField) {
                        custom_form.setFieldValue(prev.field, undefined);
                    }
                    const wait_otp_second = waitOtpSeconds.find(item=>item.field === prev.field);
                    if(!wait_otp_second.countdown && prev.inputValue && handleValidateInput(prev.inputFormat, prev.inputValue)) {
                        return { ...prev, canSend: true };
                    }
                    return { ...prev, canSend: false };
                }));
            };

            // [reCAPTCHA] build the submit body with the recaptcha proof.
            const buildBody = (proof) => ({
                formType: customFormType,
                data: format_values,
                displayData,
                formCode: customFormCode,
                ...(proof ? { recaptcha: proof } : {}),
            });

            try {
                let res = await submitCustomFormNew(buildBody(recaptchaProof), mCodeParam, sCodeParam);

                // [reCAPTCHA] v3 low score -> backend requests a v2 challenge.
                if(res?.errorCode === 'RECAPTCHA_CHALLENGE_REQUIRED' && res?.recaptchaChallenge) {
                    recaptcha.renderChallenge(res.recaptchaChallenge);
                    if(res.recaptchaChallenge.widget === 'invisible') {
                        // Auto-execute the invisible v2 widget and resubmit once.
                        const v2Proof = await recaptcha.getSubmitProof();
                        res = await submitCustomFormNew(buildBody(v2Proof), mCodeParam, sCodeParam);
                    } else {
                        // Checkbox challenge: ask the user to complete it and submit again.
                        setLoading(false);
                        messageApi.open({
                            type: 'warning',
                            content: 'Please complete the reCAPTCHA verification and submit again.',
                        });
                        return;
                    }
                }

                if(res?.success === true) {
                    const rootIds = Object.keys(formData)?.filter((key) => key.includes('root'));
                    const successMsg = rootIds?.length ? formData?.[rootIds[0]]?.data?.messageData?.successfully : null;
                    messageApi.open({
                        type: 'success',
                        duration: 5,
                        content: successMsg || res.message,
                    });
                    handleResetFormData(resetFields);
                    resetOtpState(false);
                    // [reCAPTCHA] reset v2 widget and clear fallback challenge after success.
                    recaptcha.clearChallenge();
                } else {
                    // [reCAPTCHA] surface specific recaptcha error codes.
                    const errCode = res?.errorCode;
                    let errMsg = res?.message;
                    if(errCode === 'RECAPTCHA_INVALID') {
                        errMsg = res?.message || 'reCAPTCHA verification failed, please retry.';
                        recaptcha.reset();
                    } else if(errCode === 'RECAPTCHA_NOT_CONFIGURED') {
                        errMsg = res?.message || 'Submission is temporarily unavailable (reCAPTCHA not configured).';
                    } else if(errCode === 'RECAPTCHA_REQUIRED') {
                        recaptcha.reset();
                    }
                    messageApi.open({ type: 'error', content: errMsg });
                    resetOtpState(true);
                }
                setLoading(false);
            } catch (error) {
                setLoading(false);
                messageApi.open({
                    type: 'error',
                    content: error.response?.data?.message || error.message,
                });
                resetOtpState(true);
            }
        }
        // else {
        //     const merchantCode2 = currentMerchantCode === 'uat_test' ? '' : `api/merchant/${currentMerchantCode}/form/`;
        //     submitCustomForm({
        //         formType: customFormType,
        //         data: save_values,
        //         formCode: customFormCode,
        //     }, merchantCode2).then(res => {
        //         if(res?.status === '200') {
        //             const rootIds = Object.keys(formData)?.filter((key) => key.includes('root'));
        //             const message = rootIds?.length ? formData?.[rootIds[0]]?.data?.messageData?.successfully : null;;
        //             messageApi.open({
        //                 type: 'success',
        //                 duration: 5,
        //                 content: message || res.message,
        //             });
        //             // setShowSubmitSuccess(true);
        //             for(let key in values) {
        //                 custom_form.setFieldValue(key, undefined);
        //             }
        //             setValidateOtpResult(prevData=>prevData.map(prev=>{
        //                 return {
        //                     ...prev,
        //                     res: null
        //                 }
        //             }));
        //             setValidateOtpValue(prevData=>prevData.map(prev=>{
        //                 return {
        //                     ...prev,
        //                     value: null
        //                 }
        //             }));
        //             setCanSendOtp(prevData=>prevData.map(prev=>{
        //                 const wait_otp_second = waitOtpSeconds.find(item=>item.field === prev.field);
        //                 if(!wait_otp_second.countdown && prev.inputValue && handleValidateInput(prev.inputFormat, prev.inputValue)) {
        //                     return {
        //                         ...prev,
        //                         canSend: true
        //                     }
        //                 }
        //                 else {
        //                     return {
        //                         ...prev,
        //                         canSend: false
        //                     }
        //                 }
        //             }));
        //         }
        //         else {
        //             messageApi.open({
        //                 type: 'error',
        //                 content: res.message,
        //             });
        //             setValidateOtpResult(prevData=>prevData.map(prev=>{
        //                 return {
        //                     ...prev,
        //                     res: null
        //                 }
        //             }));
        //             setValidateOtpValue(prevData=>prevData.map(prev=>{
        //                 return {
        //                     ...prev,
        //                     value: null
        //                 }
        //             }));
        //             setCanSendOtp(prevData=>prevData.map(prev=>{
        //                 custom_form.setFieldValue(prev.field, undefined);
        //                 const wait_otp_second = waitOtpSeconds.find(item=>item.field === prev.field);
        //                 if(!wait_otp_second.countdown && prev.inputValue && handleValidateInput(prev.inputFormat, prev.inputValue)) {
        //                     return {
        //                         ...prev,
        //                         canSend: true
        //                     }
        //                 }
        //                 else {
        //                     return {
        //                         ...prev,
        //                         canSend: false
        //                     }
        //                 }
        //             }));
        //         }
        //         setLoading(false);
        //     }).catch(error => {
        //         setLoading(false);
        //         messageApi.open({
        //             type: 'error',
        //             content: error.response?.data?.message || error.message,
        //         });
        //         setValidateOtpResult(prevData=>prevData.map(prev=>{
        //             return {
        //                 ...prev,
        //                 res: null
        //             }
        //         }));
        //         setValidateOtpValue(prevData=>prevData.map(prev=>{
        //             return {
        //                 ...prev,
        //                 value: null
        //             }
        //         }));
        //         setCanSendOtp(prevData=>prevData.map(prev=>{
        //             custom_form.setFieldValue(prev.field, undefined);
        //             const wait_otp_second = waitOtpSeconds.find(item=>item.field === prev.field);
        //             if(!wait_otp_second.countdown && prev.inputValue && handleValidateInput(prev.inputFormat, prev.inputValue)) {
        //                 return {
        //                     ...prev,
        //                     canSend: true
        //                 }
        //             }
        //             else {
        //                 return {
        //                     ...prev,
        //                     canSend: false
        //                 }
        //             }
        //         }));
        //     })
        // }
    }, [validateOtpValue, customFormType, messageApi, allFieldValues, custom_form, fieldItems, formData, currentMerchantCode, 
        customFormCode, waitOtpSeconds, handleValidateInput, handleSendMessage, formatMessage, handleResetFormData, handleGetFieldFormat, recaptcha]);

    //弹出错误message
    const handleAlertErrorMessage = useCallback((errorMsg)=>{
        const message_key = `custom_message_${Date.now()}`;
        messageApi.open({
            key: message_key,
            type: 'error',
            content: <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr auto',
                gap: '8px',
                alignItems: 'center',
                justifyContent: 'center'
            }}>
                {errorMsg}
                <div style={{
                    cursor: 'pointer'
                }} onClick={()=>{
                    messageApi.destroy(message_key)
                }}>
                    <CloseCircleOutlined style={{color: "#D54941"}} />
                </div>
            </div>,
            duration: 0, // 设置为0表示不自动关闭
        });
    }, [messageApi])

    //输入邮箱
    const handleChangeEmail = useCallback((val, val_field)=>{
        const otpRelatedVal = canSendOtp.find(item=>item.inputField === val_field);
        let wait_otp_second = waitOtpSeconds.find(item=>item.field === otpRelatedVal?.field);
        if(!wait_otp_second?.countdown) {
            if(handleValidateInput(otpRelatedVal?.inputFormat, val)) {
                setCanSendOtp(prevData=>prevData.map(prev=>{
                    return prev.field === otpRelatedVal?.field ? {
                        ...prev,
                        inputValue: val,
                        canSend: true
                    } : prev
                }));
            }
            else {
                setCanSendOtp(prevData=>prevData.map(prev=>{
                    return prev.field === otpRelatedVal?.field ? {
                        ...prev,
                        inputValue: val,
                        canSend: false
                    } : prev
                }));
            }
        }
        setValidateOtpResult(prevData=>prevData.map(prev=>{
            return prev.field === otpRelatedVal?.field ? {
                ...prev,
                res: null
            } : prev
        }));
        setValidateOtpValue(prevData=>prevData.map(prev=>{
            return prev.field === otpRelatedVal?.field ? {
                ...prev,
                value: null
            } : prev
        }));
        custom_form.setFieldValue(otpRelatedVal?.field, '');
        setValidateOtpLoading(prevData=>prevData.map(prev=>{
            return prev.field === otpRelatedVal?.field ? {
                ...prev,
                loading: false
            } : prev
        }));
    }, [canSendOtp, custom_form, waitOtpSeconds, handleValidateInput])

    //发送验证码
    const handleStartSendOtp = useCallback((field)=>{
        let otpRelatedVal = {};
        setCanSendOtp(prevData=>prevData.map(prev=>{
            if(prev.field === field) {
                otpRelatedVal.inputValue = prev.inputValue;
                otpRelatedVal.inputFormat = prev.inputFormat;
                return {
                    ...prev,
                    canSend: false
                }
            }
            return prev
        }));
        setOtpLoading(prevData=>prevData.map(prev=>{
            return prev.field === field ? {
                ...prev,
                loading: true
            } : prev
        }));
        setValidateOtpResult(prevData=>prevData.map(prev=>{
            return prev.field === field ? {
                ...prev,
                res: null
            } : prev
        }));
        setValidateOtpValue(prevData=>prevData.map(prev=>{
            return prev.field === field ? {
                ...prev,
                value: null
            } : prev
        }));
        sendSystemMessage({
            type: "OTP",
            id: otpRelatedVal.inputValue,
            content: {},
            merchant: currentMerchantCode === 'uat_test' ? 'demo' : currentMerchantCode,
            language: 'global'
        }, currentMerchantCode === 'uat_test' ? '' : `api/merchant/${currentMerchantCode}/warranty/`).then(res=>{
            setOtpLoading(prevData=>prevData.map(prev=>{
                return prev.field === field ? {
                    ...prev,
                    loading: false
                } : prev
            }));
            if(res?.status == '200') {
                messageApi.open({
                    type: 'info',
                    content: 'Send the verification code to your email successfully!',
                    duration: 5,
                });
                let countdown = 30;
                setWaitOtpSeconds(prevData=>prevData.map(prev=>{
                    return prev.field === field ? {
                        ...prev,
                        countdown: countdown
                    } : prev
                }));
                const tim = setInterval(function () {
                    countdown--;
                    setWaitOtpSeconds(prevData=>prevData.map(prev=>{
                        return prev.field === field ? {
                            ...prev,
                            countdown: countdown
                        } : prev
                    }));
                    if (countdown == 0 || countdown < 0) {
                        clearInterval(tim);
                        setWaitOtpSeconds(prevData=>prevData.map(prev=>{
                            return prev.field === field ? {
                                ...prev,
                                countdown: 0
                            } : prev
                        }));
                        if(otpRelatedVal.inputValue) {
                            if(handleValidateInput(otpRelatedVal?.inputFormat, otpRelatedVal?.inputValue) && !validateOtpResult && !validateOtpLoading) {
                                setCanSendOtp(prevData=>prevData.map(prev=>{
                                    if(prev.field === field) {
                                        return {
                                            ...prev,
                                            canSend: true
                                        }
                                    }
                                    return prev
                                }));
                            }
                            else {
                                setCanSendOtp(prevData=>prevData.map(prev=>{
                                    if(prev.field === field) {
                                        return {
                                            ...prev,
                                            canSend: false
                                        }
                                    }
                                    return prev
                                }));
                            }
                        }
                        else {
                            setCanSendOtp(prevData=>prevData.map(prev=>{
                                if(prev.field === field) {
                                    return {
                                        ...prev,
                                        canSend: false
                                    }
                                }
                                return prev
                            }));
                        }
                    }
                }, 1000)
            }
            else {
                const message_key = `custom_message_${Date.now()}`;
                messageApi.open({
                    key: message_key,
                    type: 'error',
                    content: <div style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr auto',
                        gap: '8px',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                        {res.message}
                        <div style={{
                            cursor: 'pointer'
                        }} onClick={()=>{
                            messageApi.destroy(message_key)
                        }}>
                            <CloseCircleOutlined style={{color: "#D54941"}} />
                        </div>
                    </div>,
                    duration: 0, // 设置为0表示不自动关闭
                });
                if(otpRelatedVal.inputValue) {
                    if(handleValidateInput(otpRelatedVal?.inputFormat, otpRelatedVal.inputValue) && !validateOtpResult && !validateOtpLoading) {
                        setCanSendOtp(prevData=>prevData.map(prev=>{
                            if(prev.field === field) {
                                return {
                                    ...prev,
                                    canSend: true
                                }
                            }
                            return prev
                        }));
                    }
                    else {
                        setCanSendOtp(prevData=>prevData.map(prev=>{
                            if(prev.field === field) {
                                return {
                                    ...prev,
                                    canSend: false
                                }
                            }
                            return prev
                        }));
                    }
                }
                else {
                    setCanSendOtp(prevData=>prevData.map(prev=>{
                        if(prev.field === field) {
                            return {
                                ...prev,
                                canSend: false
                            }
                        }
                        return prev
                    }));
                }
            }
        }).catch(error=>{
            setOtpLoading(prevData=>prevData.map(prev=>{
                return prev.field === field ? {
                    ...prev,
                    loading: false
                } : prev
            }));
            const message_key = `custom_message_${Date.now()}`;
            messageApi.open({
                key: message_key,
                type: 'error',
                content: <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr auto',
                    gap: '8px',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}>
                    {error.response?.data?.message || error.message}
                    <div style={{
                        cursor: 'pointer'
                    }} onClick={()=>{
                        messageApi.destroy(message_key)
                    }}>
                        <CloseCircleOutlined style={{color: "#D54941"}} />
                    </div>
                </div>,
                duration: 0, // 设置为0表示不自动关闭
            });
            if(otpRelatedVal.inputValue) {
                if(handleValidateInput(otpRelatedVal?.inputFormat, otpRelatedVal.inputValue) && !validateOtpResult && !validateOtpLoading) {
                    setCanSendOtp(prevData=>prevData.map(prev=>{
                        if(prev.field === field) {
                            return {
                                ...prev,
                                canSend: true
                            }
                        }
                        return prev
                    }));
                }
                else {
                    setCanSendOtp(prevData=>prevData.map(prev=>{
                        if(prev.field === field) {
                            return {
                                ...prev,
                                canSend: false
                            }
                        }
                        return prev
                    }));
                }
            }
            else {
                setCanSendOtp(prevData=>prevData.map(prev=>{
                    if(prev.field === field) {
                        return {
                            ...prev,
                            canSend: false
                        }
                    }
                    return prev
                }));
            }
        })
    }, [currentMerchantCode, messageApi, validateOtpLoading, validateOtpResult, handleValidateInput])

    //验证输入的验证码是否正确
    const handleValidateOtp = useCallback((otp, field)=>{
        setValidateOtpLoading(prevData=>prevData.map(prev=>{
            return prev.field === field ? {
                ...prev,
                loading: true
            } : prev
        }));
        let otpRelatedVal = canSendOtp.find(item=>item.field === field);
        let wait_otp_second = waitOtpSeconds.find(item=>item.field === field);
        verifyOtp({
            id: otpRelatedVal.inputValue,
            otp: otp
        }, currentMerchantCode === 'uat_test' ? '' : `api/merchant/${currentMerchantCode}/warranty/`).then(res=>{
            setValidateOtpLoading(prevData=>prevData.map(prev=>{
                return prev.field === field ? {
                    ...prev,
                    loading: false
                } : prev
            }));
            if(res?.status == 200) {
                setValidateOtpResult(prevData=>prevData.map(prev=>{
                    return prev.field === field ? {
                        ...prev,
                        res: true
                    } : prev
                }));
                setValidateOtpValue(prevData=>prevData.map(prev=>{
                    return prev.field === field ? {
                        ...prev,
                        value: res?.message
                    } : prev
                }));
            }
            else {
                setValidateOtpResult(prevData=>prevData.map(prev=>{
                    return prev.field === field ? {
                        ...prev,
                        res: false
                    } : prev
                }));
                setValidateOtpValue(prevData=>prevData.map(prev=>{
                    return prev.field === field ? {
                        ...prev,
                        value: null
                    } : prev
                }));
                if(!wait_otp_second.countdown && otpRelatedVal.inputValue) {
                    if(handleValidateInput(otpRelatedVal?.inputFormat, otpRelatedVal.inputValue)) {
                        setCanSendOtp(prevData=>prevData.map(prev=>{
                            return prev.field === field ? {
                                ...prev,
                                canSend: true
                            } : prev
                        }));
                    }
                }
            }
        }).catch(error=>{
            setValidateOtpLoading(prevData=>prevData.map(prev=>{
                return prev.field === field ? {
                    ...prev,
                    loading: false
                } : prev
            }));
            const message_key = `custom_message_${Date.now()}`;
            messageApi.open({
                key: message_key,
                type: 'error',
                content: <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr auto',
                    gap: '8px',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}>
                    {error.response?.data?.message || error.message}
                    <div style={{
                        cursor: 'pointer'
                    }} onClick={()=>{
                        messageApi.destroy(message_key)
                    }}>
                        <CloseCircleOutlined style={{color: "#D54941"}} />
                    </div>
                </div>,
                duration: 0, // 设置为0表示不自动关闭
            });
        })
    }, [canSendOtp, waitOtpSeconds, currentMerchantCode, messageApi, handleValidateInput])

    //保存验证码框输入的值，有值输入则让按钮置灰
    const handleChangeOTP = useCallback((val, field)=>{
        const otp_val = val.currentTarget.value;
        let wait_otp_second = waitOtpSeconds.find(item=>item.field === field);
        setValidateOtpResult(prevData=>prevData.map(prev=>{
            return prev.field === field ? {
                ...prev,
                res: null
            } : prev
        }));
        setValidateOtpValue(prevData=>prevData.map(prev=>{
            return prev.field === field ? {
                ...prev,
                value: null
            } : prev
        }));
        if(otp_val?.length === 6) {
            setCanSendOtp(prevData=>prevData.map(prev=>{
                if(prev.field === field) {
                    return {
                        ...prev,
                        canSend: false
                    }
                }
                return prev
            }));
            handleValidateOtp(otp_val, field);
        }
        else if(!wait_otp_second?.countdown) {
            setCanSendOtp(prevData=>prevData.map(prev=>{
                if(prev.field === field) {
                    return {
                        ...prev,
                        canSend: true
                    }
                }
                return prev
            }));
        }
    }, [handleValidateOtp, waitOtpSeconds]);

    useEffect(()=>{
        const baseFormCode = code || fCode;
        const baseMerchantCode = merchant_code || mCode;
        const baseFormType = form_type || fType;
        if((pathname.includes('/custom-form') && baseFormCode && baseMerchantCode && baseFormType) || (fCode && mCode && fType)) {
            if(fCode && mCode && fType) {
                setIsEmbed(true);
            }
            else {
                setIsEmbed(false);
            }
            setCustomFormCode(baseFormCode);
            setCurrentMerchantCode(baseMerchantCode);
            setCustomFormType(baseFormType);
            handleGetFormData(baseFormCode, baseMerchantCode, baseFormType);
        }
        else {
            setLoading(false);
        }
    }, [code, pathname, merchant_code, form_type, fCode, mCode, fType])

    return {
        isEmbed,
        customFormCode,
        currentMerchantCode,
        contextHolder,
        loading,
        customFormType,
        custom_form,
        formData,
        handleSubmitSurvey,
        showSubmitSuccess,
        handleSubmitCustomForm,
        handleAlertErrorMessage,
        otpLoading,
        canSendOtp,
        waitOtpSeconds,
        validateOtpResult,
        validateOtpLoading,
        handleStartSendOtp,
        handleChangeOTP,
        handleChangeEmail,
        allFieldValues,
        setAllFieldValues,
        formClientWidth,
        setFormClientWidth,
        htmlData,
        errorMessage,
        successMessage,
        languageOptions,
        selectedLanguageCode,
        showLanguageSelector,
        handleChangeLanguage,
        recaptcha
    }
}
